import React from "react";

const GalleryControlsBar = ({
  isGalleryControlsOpen,
  setIsGalleryControlsOpen,
  closeGalleryControls,
  galleryControlsMode,
  openGalleryControls,
  sortOption,
  handleSortChange,
  groupByServiceAppointment,
  toggleGroupByServiceAppointment,
  showGroupByServiceAppointment = false,
  selectedFileTypeGroups,
  toggleFileTypeGroup,
  capturedDateFrom,
  handleCapturedDateFromChange,
  capturedDateTo,
  handleCapturedDateToChange,
  isSingleDateMode,
  handleSingleDateModeChange,
  includeNoCapturedDate,
  handleIncludeNoCapturedDateChange,
  selectedEngineers,
  toggleEngineerSelection,
  engineerSearch,
  setEngineerSearch,
  engineerOptions,
  selectedServiceAppointments,
  toggleServiceAppointmentSelection,
  saSearch,
  setSaSearch,
  saOptions,
  clearGalleryFilters,
  showToolbar = true,
  showSidebar = true,
}) => {
  if (showSidebar && !isGalleryControlsOpen) {
    return null;
  }

  const sidebarClassName = showSidebar
    ? "download-sidebar galleryControlsSidebar"
    : "galleryControlsDock";
  const handleToolbarClick = () => {
    openGalleryControls("filter");
  };

  return (
    <div className={sidebarClassName}>
      {showToolbar && (
        <button
          type="button"
          className="galleryControlIconButton"
          onClick={handleToolbarClick}
          title="Open sort and filter options"
          aria-label="Open sort and filter options"
        >
          <svg
            viewBox="0 0 24 24"
            aria-hidden="true"
            focusable="false"
            className="galleryControlIconSvg"
          >
            <path d="M4 5h16v2l-6 7v5l-4 2v-7L4 7V5z" />
          </svg>
        </button>
      )}

      {showSidebar && (
        <>
          <div className="galleryControlsPanelHeader">
            <div className="galleryControlsHeaderCopy">
              <h5>Gallery controls</h5>
              <p>
                {galleryControlsMode === "sort"
                  ? "Choose how the images are ordered."
                  : "Narrow the images shown in the gallery."}
              </p>
            </div>
            <button
              type="button"
              className="galleryControlsCloseButton"
              onClick={closeGalleryControls}
            >
              Close
            </button>
          </div>

          <div className="galleryControlsTabs">
            <button
              type="button"
              className={`galleryControlsTab ${
                galleryControlsMode === "sort" ? "active" : ""
              }`}
              onClick={() => openGalleryControls("sort")}
            >
              Sort
            </button>
            <button
              type="button"
              className={`galleryControlsTab ${
                galleryControlsMode === "filter" ? "active" : ""
              }`}
              onClick={() => openGalleryControls("filter")}
            >
              Filter
            </button>
          </div>

          <div className="galleryControlsBody">
            {galleryControlsMode === "sort" ? (
              <section className="galleryControlSection">
                <h6>Sort</h6>
                <label className="galleryRadioOption">
                  <input
                    type="radio"
                    name="gallerySort"
                    value="capturedDateDesc"
                    checked={sortOption === "capturedDateDesc"}
                    onChange={(e) => handleSortChange(e.target.value)}
                  />
                  Sort new to oldest (Captured Date)
                </label>
                <label className="galleryRadioOption">
                  <input
                    type="radio"
                    name="gallerySort"
                    value="capturedDateAsc"
                    checked={sortOption === "capturedDateAsc"}
                    onChange={(e) => handleSortChange(e.target.value)}
                  />
                  Sort oldest to new (Captured Date)
                </label>
                <label className="galleryRadioOption">
                  <input
                    type="radio"
                    name="gallerySort"
                    value="engineer"
                    checked={sortOption === "engineer"}
                    onChange={(e) => handleSortChange(e.target.value)}
                  />
                  Sort by Engineer
                </label>
                {showGroupByServiceAppointment && (
                  <label className="galleryCheckboxOption galleryGroupByOption">
                    <input
                      type="checkbox"
                      checked={groupByServiceAppointment}
                      onChange={toggleGroupByServiceAppointment}
                    />
                    Group by Service Appointment
                  </label>
                )}
              </section>
            ) : (
              <section className="galleryControlSection">
                <div className="galleryFilterHeader">
                  <h6>Filter</h6>
                  <button
                    type="button"
                    className="galleryClearButton"
                    onClick={clearGalleryFilters}
                  >
                    Clear all
                  </button>
                </div>

                <div className="galleryFilterBlock">
                  <strong>File Type</strong>
                  <div className="galleryCheckboxGrid">
                    {["Images", "Videos", "Documents", "Others"].map(
                      (groupName) => (
                        <label
                          key={groupName}
                          className="galleryCheckboxOption"
                        >
                          <input
                            type="checkbox"
                            checked={selectedFileTypeGroups.includes(groupName)}
                            onChange={() => toggleFileTypeGroup(groupName)}
                          />
                          {groupName}
                        </label>
                      ),
                    )}
                  </div>
                </div>

                <div className="galleryFilterBlock">
                  <strong>Date Captured</strong>
                  <div className="galleryDateInputs">
                    <label>
                      From
                      <input
                        type="date"
                        value={capturedDateFrom}
                        onChange={(e) =>
                          handleCapturedDateFromChange(e.target.value)
                        }
                      />
                    </label>

                    {!isSingleDateMode && (
                      <label>
                        To
                        <input
                          type="date"
                          value={capturedDateTo}
                          onChange={(e) =>
                            handleCapturedDateToChange(e.target.value)
                          }
                        />
                      </label>
                    )}
                  </div>

                  <label className="galleryCheckboxOption">
                    <input
                      type="checkbox"
                      checked={isSingleDateMode}
                      onChange={(e) =>
                        handleSingleDateModeChange(e.target.checked)
                      }
                    />
                    Select Single Date
                  </label>

                  <label className="galleryCheckboxOption">
                    <input
                      type="checkbox"
                      checked={includeNoCapturedDate}
                      onChange={(e) =>
                        handleIncludeNoCapturedDateChange(e.target.checked)
                      }
                    />
                    Include files with no Captured Date
                  </label>
                </div>

                <div className="galleryFilterBlock">
                  <strong>Engineer</strong>
                  <input
                    type="search"
                    className="gallerySearchInput"
                    placeholder="Search engineer names"
                    value={engineerSearch}
                    onChange={(e) => setEngineerSearch(e.target.value)}
                  />
                  <div className="galleryEngineerList">
                    {engineerOptions
                      .filter((engineerName) =>
                        engineerName
                          .toLowerCase()
                          .includes(engineerSearch.toLowerCase()),
                      )
                      .map((engineerName) => (
                        <label
                          key={engineerName}
                          className="galleryCheckboxOption"
                        >
                          <input
                            type="checkbox"
                            checked={selectedEngineers.includes(engineerName)}
                            onChange={() =>
                              toggleEngineerSelection(engineerName)
                            }
                          />
                          {engineerName}
                        </label>
                      ))}
                    {engineerOptions.length === 0 && (
                      <span className="galleryEmptyState">
                        No engineers available for this selection.
                      </span>
                    )}
                  </div>
                </div>

                <div className="galleryFilterBlock">
                  <strong>SA No.</strong>
                  <input
                    type="search"
                    className="gallerySearchInput"
                    placeholder="Search SA numbers"
                    value={saSearch}
                    onChange={(e) => setSaSearch(e.target.value)}
                  />
                  <div className="galleryEngineerList">
                    {saOptions
                      .filter((saNumber) =>
                        saNumber.toLowerCase().includes(saSearch.toLowerCase()),
                      )
                      .map((saNumber) => (
                        <label key={saNumber} className="galleryCheckboxOption">
                          <input
                            type="checkbox"
                            checked={selectedServiceAppointments.includes(
                              saNumber,
                            )}
                            onChange={() =>
                              toggleServiceAppointmentSelection(saNumber)
                            }
                          />
                          {saNumber}
                        </label>
                      ))}
                    {saOptions.length === 0 && (
                      <span className="galleryEmptyState">
                        No SA numbers available for this selection.
                      </span>
                    )}
                  </div>
                </div>
              </section>
            )}
          </div>
        </>
      )}
    </div>
  );
};

export default GalleryControlsBar;
